import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Returns a `goBack` callback that navigates one entry back in the history stack
 * when there is a usable previous entry, otherwise falls back to `fallbackPath`.
 *
 * "Usable previous entry" = either an in-app SPA navigation (location.key !== 'default')
 * OR a same-origin `document.referrer` (covers cases where the user arrived via a
 * full-page `<a href>` link from inside our own site).
 *
 * This avoids stranding users on a "dead end" when they open a page directly via
 * URL / refresh / new tab — those scenarios have no usable back entry, so we fall
 * back to the home page.
 */
export default function useSmartBack(fallbackPath = '/') {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    const inAppNav = location.key && location.key !== 'default';
    let sameOriginReferrer = false;
    try {
      if (document.referrer) {
        sameOriginReferrer = new URL(document.referrer).origin === window.location.origin;
      }
    } catch (_e) {
      sameOriginReferrer = false;
    }

    if (inAppNav || sameOriginReferrer) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  }, [navigate, location.key, fallbackPath]);
}
